import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Ticker from './Ticker/Ticker';
import Book from './Book/Book';
import useWebSocket from 'react-use-websocket';
import Wallet from '../../components/Wallet/Wallet';
import NewOrderButton from '../../components/NewOrder/NewOrderButton';
import NewOrderModal from '../../components/NewOrder/NewOrderModal';
import CandleChart from './CandleChart';
import SelectSymbol from '../../components/SelectSymbol';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import QuickStrategyModal from '../../components/QuickStrategy/QuickStrategyModal';
import Market24hCard from './Market24hCard';
import BookCard from './BookCard';
import DashboardWalletCard from './DashboardWalletCard';
import { showNotification } from '../../utils/notify';
import { getSettings } from '../../services/SettingsService';
import './Dashboard.css';

function Dashboard() {

  const navigate = useNavigate();

  const [tickerState, setTickerState] = useState({});

  const [balanceState, setBalanceState] = useState({});

  const [bookState, setBookState] = useState({});

  const [wallet, setWallet] = useState({});

  const [chartSymbol, setChartSymbol] = useState('BTCUSDT');

  const [notification, setNotification] = useState({ type: '', text: '' });
  const lastBalanceAt = useRef(0);
  const balanceRefreshMs = useRef(60 * 60 * 1000);

  useEffect(() => {
    getSettings()
      .then(result => {
        const hours = Math.max(1, parseInt(result?.walletBalanceRefreshHours, 10) || 1);
        balanceRefreshMs.current = hours * 60 * 60 * 1000;
      })
      .catch(() => { /* mantém 1 hora */ });
  }, []);

  const { lastJsonMessage } = useWebSocket(process.env.REACT_APP_WS_URL, {
    onOpen: () => {
      console.log(`Connected to App WS`);
    },
    onMessage: () => {
      if (lastJsonMessage) {
        if (lastJsonMessage.ticker) setTickerState(lastJsonMessage.ticker);
        else if (lastJsonMessage.balance) {
          const now = Date.now();
          if (lastBalanceAt.current && now - lastBalanceAt.current < balanceRefreshMs.current)
            return;
          lastBalanceAt.current = now;
          setBalanceState(lastJsonMessage.balance);
        }
        else if (lastJsonMessage.book) {
          const next = {};
          lastJsonMessage.book.forEach(b => { next[b.symbol] = b; });
          setBookState(next);
        }
      }
    },
    queryParams: { 'token': localStorage.getItem("token") },
    share: true,
    onError: (event) => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden')
        return;
      console.error(event);
    },
    shouldReconnect: (closeEvent) => true,
    reconnectInterval: 3000
  });

  function onWalletUpdate(walletObj) {
    setWallet(walletObj);
  }

  function onSubmitOrder(order) {
    navigate('/orders/' + order.symbol);
  }

  function onChangeSymbol(event) {
    setChartSymbol(event.target.value);
  }

  return (
    <>
      <Menu />
      <main className="content">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <div className="dashboard-header-main">
              <h1>EasyCripto</h1>
              <div className="dashboard-header-symbol">
                <SelectSymbol onChange={onChangeSymbol} symbol={chartSymbol} />
              </div>
            </div>
            <div className="dashboard-header-actions d-flex align-items-center gap-3 flex-wrap">
              <button 
                className="btn btn-primary" 
                data-bs-toggle={localStorage.getItem("hasQuickStrategy") === 'true' ? "modal" : undefined}
                data-bs-target={localStorage.getItem("hasQuickStrategy") === 'true' ? "#modalQuickStrategy" : undefined}
                title="Criar Estratégia Rápida"
                onClick={() => {
                  if (localStorage.getItem("hasQuickStrategy") !== 'true') {
                    const limitName = localStorage.getItem("limitName") || 'Gold';
                    showNotification(`Função disponível apenas para usuários ${limitName} que têm esta função ativa.`, 'info');
                  }
                }}
                style={{
                  background: 'linear-gradient(90deg, #8c66fc 0%, #0274d8 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  fontWeight: 600,
                  opacity: localStorage.getItem("hasQuickStrategy") === 'true' ? 1 : 0.6,
                  cursor: localStorage.getItem("hasQuickStrategy") === 'true' ? 'pointer' : 'not-allowed'
                }}
              >
                <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Estratégia Rápida
              </button>
              <NewOrderButton />
            </div>
          </div>
          
          <CandleChart symbol={chartSymbol} />
          
          <div className="dashboard-cards-grid">
            <Market24hCard data={tickerState} />
            <BookCard data={bookState} />
            <DashboardWalletCard data={balanceState} onUpdate={onWalletUpdate} />
          </div>
        </div>
        <Footer />
      </main>
      <NewOrderModal wallet={wallet} onSubmit={onSubmitOrder} />
      <QuickStrategyModal onNotification={setNotification} />
      <Toast type={notification.type} text={notification.text} />
    </>
  );
}

export default Dashboard;
